import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { getCurrentTenant } from '../utils/tenantUtils';
import apiService from '../services/apiService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentTenant, setCurrentTenant] = useState(getCurrentTenant());
  const [showSessionWarning, setShowSessionWarning] = useState(false);

  // Configuración de tiempos de sesión (en milisegundos)
  const INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutos de inactividad
  const MAX_SESSION_TIME = 8 * 60 * 60 * 1000; // 8 horas máximo
  const WARNING_TIME = 2 * 60 * 1000; // Advertencia 2 minutos antes

  useEffect(() => {
    // Verificar token y cargar usuario
    const initAuth = async () => {
      const token = localStorage.getItem('authToken');
      if (token) {
        try {
          const userData = await apiService.getCurrentUser();
          console.log('👤 Usuario cargado:', userData);
          
          // Validar y limpiar tiempos de sesión
          const now = Date.now();
          const sessionStart = parseInt(localStorage.getItem('sessionStartTime') || '0');
          const lastActivity = parseInt(localStorage.getItem('lastActivityTime') || '0');
          
          // Si los tiempos son inválidos, futuros, o muy antiguos (>24h), reinicializarlos
          if (!sessionStart || sessionStart > now || sessionStart < now - 24 * 60 * 60 * 1000 ||
              !lastActivity || lastActivity > now || lastActivity < now - 24 * 60 * 60 * 1000) {
            console.log('⚠️ Tiempos de sesión inválidos, reinicializando...');
            localStorage.setItem('sessionStartTime', now.toString());
            localStorage.setItem('lastActivityTime', now.toString());
          } else {
            // Solo actualizar lastActivityTime si los tiempos son válidos
            localStorage.setItem('lastActivityTime', now.toString());
          }
          
          setUser(userData); // El endpoint /me devuelve directamente los datos del usuario
        } catch (error) {
          console.error('Error loading user:', error);
          localStorage.removeItem('authToken');
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  // Gestión de expiración de sesión
  useEffect(() => {
    if (!user) return;

    let inactivityTimer;
    let warningTimer;
    let maxSessionTimer;

    const updateActivity = () => {
      const now = Date.now();
      localStorage.setItem('lastActivityTime', now.toString());
      setShowSessionWarning(false);
      
      // Reiniciar timers
      clearTimeout(inactivityTimer);
      clearTimeout(warningTimer);
      
      // Timer para mostrar advertencia
      warningTimer = setTimeout(() => {
        setShowSessionWarning(true);
      }, INACTIVITY_TIMEOUT - WARNING_TIME);
      
      // Timer para cerrar sesión por inactividad
      inactivityTimer = setTimeout(() => {
        console.log('🔒 Sesión cerrada por inactividad');
        logout();
      }, INACTIVITY_TIMEOUT);
    };

    // Verificar tiempo máximo de sesión
    const checkMaxSession = () => {
      const sessionStart = parseInt(localStorage.getItem('sessionStartTime') || '0');
      const now = Date.now();
      
      // Validar que sessionStart sea un timestamp válido y no sea futuro
      if (!sessionStart || sessionStart > now || sessionStart < now - 24 * 60 * 60 * 1000) {
        // Si el timestamp es inválido o es de hace más de 24 horas, reinicializarlo
        localStorage.setItem('sessionStartTime', now.toString());
        localStorage.setItem('lastActivityTime', now.toString());
        return;
      }
      
      if (now - sessionStart >= MAX_SESSION_TIME) {
        console.log('🔒 Sesión cerrada por tiempo máximo');
        logout();
      }
    };

    // Eventos de actividad del usuario
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach(event => window.addEventListener(event, updateActivity));

    // Verificar tiempo máximo cada minuto
    maxSessionTimer = setInterval(checkMaxSession, 60 * 1000);

    // Inicializar actividad
    updateActivity();

    return () => {
      events.forEach(event => window.removeEventListener(event, updateActivity));
      clearTimeout(inactivityTimer);
      clearTimeout(warningTimer);
      clearInterval(maxSessionTimer);
    };
  }, [user]);

  const extendSession = () => {
    const now = Date.now();
    localStorage.setItem('lastActivityTime', now.toString());
    setShowSessionWarning(false);
  };

  // Escuchar cambios en el tenant
  useEffect(() => {
    const handleTenantChange = (event) => {
      const newTenant = event.detail ? event.detail.newTenant : getCurrentTenant();
      if (newTenant !== currentTenant) {
        console.log('🔄 AuthContext - Tenant changed from', currentTenant, 'to', newTenant);
        setCurrentTenant(newTenant);
      }
    };

    // Escuchar evento personalizado de cambio de tenant (inmediato)
    window.addEventListener('tenantChanged', handleTenantChange);
    
    // Escuchar cambios en localStorage (para sincronización entre pestañas)
    window.addEventListener('storage', handleTenantChange);
    
    // Backup: verificar cada segundo por si acaso (mantener por compatibilidad)
    const checkTenant = setInterval(() => {
      const newTenant = getCurrentTenant();
      if (newTenant !== currentTenant) {
        console.log('🔄 AuthContext - Tenant changed (polling) from', currentTenant, 'to', newTenant);
        setCurrentTenant(newTenant);
      }
    }, 1000);

    return () => {
      window.removeEventListener('tenantChanged', handleTenantChange);
      window.removeEventListener('storage', handleTenantChange);
      clearInterval(checkTenant);
    };
  }, [currentTenant]);

  const login = async (email, password) => {
    try {
      setLoading(true);
      const response = await apiService.login(email, password);
      
      if (response.success) {
        const { user: userData, token } = response;
        
        // Limpiar tiempos de sesión antiguos
        localStorage.removeItem('sessionStartTime');
        localStorage.removeItem('lastActivityTime');
        
        // Guardar token, usuario y tenant actual
        localStorage.setItem('authToken', token);
        localStorage.setItem('currentTenant', currentTenant); // Guardar el tenant actual
        
        // Inicializar nuevos tiempos de sesión
        const now = Date.now();
        localStorage.setItem('sessionStartTime', now.toString());
        localStorage.setItem('lastActivityTime', now.toString());
        
        setUser(userData);
        return { success: true, user: userData };
      }
      
      // Si falla por acceso denegado, propagar información de empresas disponibles
      if (response.error === 'Sin acceso a esta empresa' && response.availableCompanies) {
        return { 
          success: false, 
          error: response.error,
          availableCompanies: response.availableCompanies
        };
      }
      
      return { success: false, error: response.message || response.error || 'Credenciales inválidas' };
    } catch (error) {
      console.error('Login error:', error);
      
      // Si el error tiene información del servidor, usarla
      if (error.response?.data) {
        const errorData = error.response.data;
        return {
          success: false,
          error: errorData.error || errorData.message || 'Error en el login',
          availableCompanies: errorData.availableCompanies
        };
      }
      
      return { success: false, error: error.message || 'Error en el login' };
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('authToken');
    localStorage.removeItem('current_tenant');
    localStorage.removeItem('sessionStartTime');
    localStorage.removeItem('lastActivityTime');
    setShowSessionWarning(false);
  };

  // Verificar si es administrador global
  const isGlobalAdmin = user?.role === 'global_admin';

  // Verificar si es administrador en el tenant actual
  const isCompanyAdmin = useMemo(() => {
    if (!user || !currentTenant) return false;
    if (isGlobalAdmin) return true; // Global admin es admin en todas las empresas
    
    // Buscar el rol en la empresa actual
    const currentCompany = user.companies?.find(c => c.id === currentTenant);
    return currentCompany?.role === 'admin';
  }, [user, currentTenant, isGlobalAdmin]);

  // Verificar si tiene permisos de escritura (admin o agent)
  const canWrite = useMemo(() => {
    if (!user || !currentTenant) return false;
    if (isGlobalAdmin) return true;
    
    const currentCompany = user.companies?.find(c => c.id === currentTenant);
    return ['admin', 'agent'].includes(currentCompany?.role);
  }, [user, currentTenant, isGlobalAdmin]);

  // Verificar si es viewer (solo lectura)
  const isViewer = useMemo(() => {
    if (!user || !currentTenant) return false;
    if (isGlobalAdmin) return false;
    
    const currentCompany = user.companies?.find(c => c.id === currentTenant);
    return currentCompany?.role === 'viewer';
  }, [user, currentTenant, isGlobalAdmin]);

  // Compatibilidad con código antiguo
  const isAdmin = isGlobalAdmin || isCompanyAdmin;

  // Obtener empresas disponibles para el usuario (usar useMemo para estabilidad)
  const availableCompanies = useMemo(() => {
    if (!user) {
      console.log('🏢 availableCompanies: No user');
      return [];
    }
    
    if (isGlobalAdmin) {
      console.log('🏢 availableCompanies: Global admin, returning all companies');
      return [
        { id: 'chirakChile', name: 'Chirak Turismo Chile', role: 'admin' },
        { id: 'chirakPeru', name: 'Chirak Turismo Perú', role: 'admin' }
      ];
    }
    
    // El backend devuelve companies con estructura: { id, name, role }
    const companies = (user.companies || []).map(company => ({
      id: company.id,
      name: company.name,
      role: company.role || 'agent'
    }));
    
    console.log('🏢 availableCompanies:', {
      userEmail: user.email,
      userCompanies: user.companies,
      mappedCompanies: companies
    });
    
    return companies;
  }, [user, isGlobalAdmin]);

  const value = {
    user,
    login,
    logout,
    loading,
    isAdmin,
    isGlobalAdmin,
    isCompanyAdmin,
    canWrite,
    isViewer,
    currentTenant,
    setCurrentTenant,
    availableCompanies,
    showSessionWarning,
    extendSession
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;